import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { BarChart3, Filter } from "lucide-react";
import { api } from "@/lib/api";
import { usePermissions } from "@/contexts/PermissionContext";
import ClassAnalyticsView from "@/components/analytics/ClassAnalyticsView";
import EvaluationReportsView from "@/components/analytics/EvaluationReportsView";
import ClassComparisonView from "@/components/analytics/ClassComparisonView";
import { AnalyticsTabs, EmptyAnalytics } from "@/components/analytics/AnalyticsPrimitives";
import DoxaSelect from "@/components/doxa/DoxaSelect";

export default function Reports() {
  const { can } = usePermissions();
  const [classGroups, setClassGroups] = useState([]);
  const [loadingClasses, setLoadingClasses] = useState(true);
  const [searchParams, setSearchParams] = useSearchParams();
  const [cgId, setCgId] = useState(searchParams.get("class") || "");
  const [section, setSection] = useState(searchParams.get("view") || "class");

  const sections = useMemo(() => [
    { value: "class", label: "Turma" },
    ...(can("eval.view_results") ? [{ value: "evaluations", label: "Avaliações" }] : []),
    { value: "compare", label: "Comparações" },
  ], [can]);

  useEffect(() => {
    if (!sections.some((item) => item.value === section)) setSection("class");
  }, [sections, section]);

  useEffect(() => {
    let alive = true;
    api.get("/class-groups")
      .then(({ data }) => {
        if (!alive) return;
        setClassGroups(data);
        const requested = searchParams.get("class");
        const validRequested = requested && data.some((row) => row.id === requested);
        const next = validRequested ? requested : (data[0]?.id || "");
        setCgId(next);
        if (next && next !== requested) {
          const params = new URLSearchParams(searchParams);
          params.set("class", next);
          setSearchParams(params, { replace: true });
        }
      })
      .catch(() => alive && setClassGroups([]))
      .finally(() => alive && setLoadingClasses(false));
    return () => { alive = false; };
  }, []);

  const updateParams = (nextClass, nextSection) => {
    const params = new URLSearchParams();
    if (nextClass) params.set("class", nextClass);
    if (nextSection && nextSection !== "class") params.set("view", nextSection);
    setSearchParams(params, { replace: true });
  };

  const changeClass = (value) => {
    setCgId(value);
    updateParams(value, section);
  };

  const changeSection = (value) => {
    setSection(value);
    updateParams(cgId, value);
  };

  return (
    <div className="p-4 sm:p-6 md:p-8 max-w-7xl mx-auto" data-testid="reports-page">
      <header className="mb-6 flex items-start justify-between gap-4 flex-wrap">
        <div>
          <p className="text-xs uppercase tracking-[0.18em] text-[#6B655E] font-medium">Central de analytics</p>
          <h1 className="font-display text-3xl md:text-4xl font-bold inline-flex items-center gap-2 mt-1"><BarChart3 className="w-7 h-7 text-[#F75A1B]" /> Relatórios 2.0</h1>
          <p className="text-sm text-[#6B655E] mt-2 max-w-2xl">Explore presença, engajamento, avaliações e comparações com drilldowns. Os relatórios continuam sob demanda para preservar desempenho.</p>
        </div>
        <span className="text-xs rounded-full bg-[#FFEBE3] text-[#F75A1B] px-3 py-1.5 font-medium">R2.2 · Inteligência ampliada</span>
      </header>

      <div className="mb-4"><AnalyticsTabs value={section} onChange={changeSection} items={sections} /></div>

      {section !== "compare" && (
        <div className="doxa-card p-3 sm:p-4 mb-5 flex flex-col sm:flex-row sm:items-center gap-3" data-testid="reports-global-filters">
          <div className="w-9 h-9 rounded-xl bg-[#F6EFE4] text-[#6B655E] flex items-center justify-center shrink-0"><Filter className="w-4 h-4" /></div>
          <div className="min-w-0 flex-1">
            <span className="text-xs text-[#6B655E] font-medium block mb-1">Turma analisada</span>
            <DoxaSelect
              value={cgId}
              onValueChange={changeClass}
              options={classGroups.map((group) => ({ value: group.id, label: group.name }))}
              className="w-full sm:max-w-md"
              testId="reports-class-select"
              ariaLabel="Turma analisada"
            />
          </div>
          {cgId && <div className="text-xs text-[#6B655E] sm:text-right">Visão contextual da turma selecionada.</div>}
        </div>
      )}

      {loadingClasses ? (
        <div className="h-40 rounded-2xl bg-[#F0EBE3] animate-pulse" />
      ) : classGroups.length === 0 ? (
        <EmptyAnalytics title="Nenhuma turma disponível" description="Cadastre ou ative uma turma para começar a análise." />
      ) : section === "class" ? (
        <ClassAnalyticsView classGroupId={cgId} compactHeader />
      ) : section === "evaluations" ? (
        <EvaluationReportsView classGroupId={cgId} />
      ) : (
        <ClassComparisonView classGroups={classGroups} initialClassId={cgId} />
      )}
    </div>
  );
}
